/**
 * F14 — Bandeja de traslados del club, para incrustar en la página de
 * Solicitudes.
 *
 * Dos lados de la misma mesa:
 *   · ENTRANTES: otros clubes piden llevarse a alguien de aquí. Aprobar
 *     ejecuta el traslado completo en una transacción; rechazar solo cierra
 *     la solicitud. Siempre con el teléfono del director que pide, porque
 *     esto se habla antes de decidirse.
 *   · SALIENTES: lo que este club pidió, con su estado y el contacto del
 *     club de origen para hacerle seguimiento.
 */
import { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { memberTransferService } from "@/lib/services/memberTransfers";
import type { MemberTransferRequest } from "@/types";
import { Button } from "@/components/ui";
import { useConfirm } from "@/components/ui";
import { toast } from "react-hot-toast";
import { ArrowLeftRight, Phone, Mail, Clock, CheckCircle2, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

const hace = (r: MemberTransferRequest) =>
  r.createdAt?.toDate
    ? formatDistanceToNow(r.createdAt.toDate(), { addSuffix: true, locale: es })
    : "";

const ESTADO: Record<
  MemberTransferRequest["status"],
  { label: string; clase: string }
> = {
  pending: {
    label: "Pendiente",
    clase:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  },
  completed: {
    label: "Trasladado",
    clase:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  },
  rejected: {
    label: "Rechazada",
    clase: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  },
  cancelled: {
    label: "Cancelada",
    clase: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
};

function Contacto({ nombre, telefono, correo }: { nombre?: string; telefono?: string; correo?: string }) {
  if (!nombre && !telefono && !correo) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
      {nombre && <span className="font-medium text-gray-700 dark:text-gray-300">{nombre}</span>}
      {telefono && (
        <a href={`tel:${telefono.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 hover:text-primary-600">
          <Phone className="w-3 h-3" /> {telefono}
        </a>
      )}
      {correo && (
        <a href={`mailto:${correo}`} className="inline-flex items-center gap-1 hover:text-primary-600">
          <Mail className="w-3 h-3" /> {correo}
        </a>
      )}
    </div>
  );
}

export default function TransferRequestsSection({ clubId }: { clubId: string }) {
  const { user } = useAuthStore();
  const confirm = useConfirm();
  const [entrantes, setEntrantes] = useState<MemberTransferRequest[]>([]);
  const [salientes, setSalientes] = useState<MemberTransferRequest[]>([]);
  const [procesando, setProcesando] = useState<string | null>(null);

  useEffect(() => {
    if (!clubId) return;
    return memberTransferService.watchForClub(
      clubId,
      (data) => {
        setEntrantes(data.entrantes);
        setSalientes(data.salientes);
      },
      (e) => console.error("[traslados]", e),
    );
  }, [clubId]);

  if (entrantes.length === 0 && salientes.length === 0) return null;

  const aprobar = async (r: MemberTransferRequest) => {
    if (!user) return;
    const ok = await confirm({
      title: `¿Trasladar a ${r.personName}?`,
      description:
        `Su membresía pasará de ${r.originClubName} a ${r.targetClubName} con ` +
        `su ficha e historial. Este club deja de verla en su lista. La pide ` +
        `${r.requestedByName}${r.requestedByPhone ? ` (${r.requestedByPhone})` : ""}.` +
        (r.originRole
          ? ` Su cargo actual (${r.originRole}) no viaja: llega como miembro y ` +
            `el club nuevo asigna el suyo.`
          : ""),
      confirmText: "Aprobar traslado",
      cancelText: "Todavía no",
      variant: "warning",
    });
    if (!ok) return;
    setProcesando(r.id);
    try {
      await memberTransferService.approve(r, user.uid);
      toast.success(`${r.personName} trasladada a ${r.targetClubName}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo aprobar");
    } finally {
      setProcesando(null);
    }
  };

  const rechazar = async (r: MemberTransferRequest) => {
    if (!user) return;
    setProcesando(r.id);
    try {
      await memberTransferService.reject(r.id, user.uid);
      toast(`Solicitud de ${r.targetClubName} rechazada`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo rechazar");
    } finally {
      setProcesando(null);
    }
  };

  const cancelar = async (r: MemberTransferRequest) => {
    if (!user) return;
    setProcesando(r.id);
    try {
      await memberTransferService.cancel(r.id, user.uid);
      toast(`Solicitud para ${r.personName} cancelada`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo cancelar");
    } finally {
      setProcesando(null);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
        <ArrowLeftRight className="w-5 h-5 text-primary-600" />
        Traslados de club
      </h2>

      {entrantes.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Otros clubes piden el traslado de miembros de tu club. Aprobar mueve
            la membresía completa; la ficha y su historial viajan con la
            persona.
          </p>
          {entrantes.map((r) => (
            <div
              key={r.id}
              className="bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800 rounded-xl p-4 space-y-2"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {r.personName}{" "}
                    <span className="font-normal text-xs text-gray-500">
                      {r.personDocMasked}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {r.originClubName} → <b>{r.targetClubName}</b>
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" /> {hace(r)}
                    </span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => aprobar(r)}
                    disabled={procesando === r.id}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rechazar(r)}
                    disabled={procesando === r.id}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Rechazar
                  </Button>
                </div>
              </div>
              {r.note && (
                <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                  “{r.note}”
                </p>
              )}
              {r.personHasGuardian && (
                <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                  Está a cargo de un acudiente. Confirma el traslado con la
                  familia antes de aprobar — el vínculo con el acudiente se
                  conserva tal cual.
                </p>
              )}
              <Contacto
                nombre={`Solicita: ${r.requestedByName}`}
                telefono={r.requestedByPhone}
                correo={r.requestedByEmail}
              />
            </div>
          ))}
        </div>
      )}

      {salientes.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Solicitudes que tu club envió a otros clubes.
          </p>
          {salientes.map((r) => (
            <div
              key={r.id}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-2"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {r.personName}{" "}
                    <span className="font-normal text-xs text-gray-500">
                      {r.personDocMasked}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {r.originClubName} → {r.targetClubName}
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" /> {hace(r)}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO[r.status].clase}`}
                  >
                    {ESTADO[r.status].label}
                  </span>
                  {r.status === "pending" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => cancelar(r)}
                      disabled={procesando === r.id}
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
              {r.status === "pending" && (
                <Contacto
                  nombre={
                    r.originDirectorName
                      ? `Aprueba: ${r.originDirectorName} (${r.originClubName})`
                      : undefined
                  }
                  telefono={r.originDirectorPhone}
                />
              )}
              {r.status === "rejected" && r.decisionNote && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  Motivo: {r.decisionNote}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
